import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Home, Maximize, Minimize } from 'lucide-react';
import { educationBlock, branches } from './slidesData';
import {
  TitleSlide,
  SplitSlide,
  BentoSlide,
  StatSlide,
  HierarchySlide,
  PhasesSlide,
  MistakesSlide,
  TestingSlide,
  ClosingSlide,
  BranchSelector,
  PhysicsSlide,
  BiologySlide,
  IntroSlide,
  VideoSlide,
  GenderSelector,
} from './slides';

// Gender context for passing selection to all slides
export const GenderContext = createContext('male');

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const renderSlide = (slide, gender) => {
  // Get gender-specific image/video if available
  const genderSlide = {
    ...slide,
    image: slide.images?.[gender] || slide.image,
    video: slide.videos?.[gender] || slide.video,
  };

  switch (slide.type) {
    case 'title_slide':
      return <TitleSlide slide={genderSlide} />;
    case 'split_slide':
      return <SplitSlide slide={genderSlide} />;
    case 'bento_slide':
      return <BentoSlide slide={genderSlide} />;
    case 'stat_slide':
      return <StatSlide slide={genderSlide} />;
    case 'hierarchy_slide':
      return <HierarchySlide slide={genderSlide} />;
    case 'phases_slide':
      return <PhasesSlide slide={genderSlide} />;
    case 'mistakes_slide':
      return <MistakesSlide slide={genderSlide} />;
    case 'testing_slide':
      return <TestingSlide slide={genderSlide} />;
    case 'closing_slide':
      return <ClosingSlide slide={genderSlide} />;
    case 'physics_slide':
      return <PhysicsSlide slide={genderSlide} />;
    case 'biology_slide':
      return <BiologySlide slide={genderSlide} />;
    case 'intro_slide':
      return <IntroSlide slide={genderSlide} />;
    case 'video_slide':
      return <VideoSlide slide={genderSlide} />;
    default:
      return <TitleSlide slide={genderSlide} />;
  }
};

// Get branch color
const getBranchColor = (branchId) => {
  const branch = branches.find(b => b.id === branchId);
  return branch?.color || '#D31113';
};

// Get branch label
const getBranchLabel = (branchId) => {
  if (!branchId) return 'Education Block';
  const branch = branches.find(b => b.id === branchId);
  return branch?.title || 'Speed Training';
};

export const Presentation = () => {
  const [[currentSlide, direction], setCurrentSlide] = useState([0, 0]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showBranchSelector, setShowBranchSelector] = useState(false);
  const [selectedGender, setSelectedGender] = useState(null); // null = show gender selector
  const [showGenderSelector, setShowGenderSelector] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);
  const controlsTimerRef = useRef(null);

  // Fullscreen API
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Auto-hide controls in fullscreen
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    if (isFullscreen) {
      controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) { setShowControls(true); return; }
    resetControlsTimer();
    return () => { if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current); };
  }, [isFullscreen, resetControlsTimer]);

  // Touch swipe support - defined after paginate (see below)

  // Build current slides array based on state
  const getCurrentSlides = useCallback(() => {
    if (showBranchSelector) {
      return [...educationBlock, { id: 'branch-selector', type: 'branch_selector' }];
    }
    if (selectedBranch) {
      const branch = branches.find(b => b.id === selectedBranch);
      return [...educationBlock, ...branch.slides];
    }
    return educationBlock;
  }, [selectedBranch, showBranchSelector]);

  const currentSlides = getCurrentSlides();
  const totalSlides = currentSlides.length;
  const currentSlideData = currentSlides[currentSlide];

  const paginate = useCallback((newDirection) => {
    setCurrentSlide(([prev]) => {
      const next = prev + newDirection;
      
      // If we're at the end of education block and no branch selected, show selector
      if (next >= educationBlock.length && !selectedBranch && !showBranchSelector) {
        setShowBranchSelector(true);
        return [educationBlock.length, newDirection];
      }
      
      if (next < 0 || next >= totalSlides) return [prev, 0];
      return [next, newDirection];
    });
  }, [totalSlides, selectedBranch, showBranchSelector]);

  // Touch swipe support
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    resetControlsTimer();
  }, [resetControlsTimer]);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) paginate(1);
    else paginate(-1);
  }, [paginate]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(([prev]) => [index, index > prev ? 1 : -1]);
  }, []);

  const handleSelectBranch = useCallback((branchId) => {
    setSelectedBranch(branchId);
    setShowBranchSelector(false);
    setCurrentSlide([educationBlock.length, 1]);
  }, []);

  const handleReturnToSelector = useCallback(() => {
    setSelectedBranch(null);
    setShowBranchSelector(true);
    setCurrentSlide([educationBlock.length, -1]);
  }, []);

  const handleGoHome = useCallback(() => {
    setSelectedBranch(null);
    setShowBranchSelector(false);
    setCurrentSlide([0, -1]);
  }, []);

  const handleSelectGender = useCallback((gender) => {
    setSelectedGender(gender);
    setShowGenderSelector(false);
  }, []);

  const handleResetGender = useCallback(() => {
    setShowGenderSelector(true);
    setSelectedGender(null);
    setSelectedBranch(null);
    setShowBranchSelector(false);
    setCurrentSlide([0, 0]);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Gender selection with M/F keys
      if (showGenderSelector) {
        if (e.key === 'm' || e.key === 'M') {
          e.preventDefault();
          handleSelectGender('male');
          return;
        } else if (e.key === 'f' || e.key === 'F') {
          e.preventDefault();
          handleSelectGender('female');
          return;
        }
        return; // Don't process other keys during gender selection
      }

      // Branch selection with number keys
      if (showBranchSelector) {
        if (e.key === '1') {
          e.preventDefault();
          handleSelectBranch('speed');
          return;
        } else if (e.key === '2') {
          e.preventDefault();
          handleSelectBranch('jumps');
          return;
        } else if (e.key === '3') {
          e.preventDefault();
          handleSelectBranch('throws');
          return;
        }
      }

      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        paginate(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        paginate(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        handleGoHome();
      } else if (e.key === 'End') {
        e.preventDefault();
        goToSlide(totalSlides - 1);
      } else if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        handleReturnToSelector();
      } else if (e.key === 'g' || e.key === 'G') {
        // 'G' key to go back to gender selector
        e.preventDefault();
        handleResetGender();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate, goToSlide, totalSlides, showBranchSelector, showGenderSelector, handleSelectBranch, handleGoHome, handleReturnToSelector, handleSelectGender, handleResetGender, toggleFullscreen]);

  // Show gender selector first
  if (showGenderSelector) {
    return (
      <GenderContext.Provider value={selectedGender}>
        <div
          ref={containerRef}
          className="h-screen w-screen bg-[#17072F] overflow-hidden relative slide-container"
          data-testid="presentation-container"
        >
          <div className="noise-overlay" />
          <GenderSelector onSelect={handleSelectGender} />
          {/* Fullscreen button */}
          <button
            onClick={toggleFullscreen}
            className="absolute bottom-8 right-8 z-50 rounded-full bg-white/5 hover:bg-white/15 text-white p-3 backdrop-blur-sm border border-white/10 transition-all"
            data-testid="fullscreen-btn"
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </GenderContext.Provider>
    );
  }

  const progress = ((currentSlide + 1) / totalSlides) * 100;
  const accentColor = selectedBranch ? getBranchColor(selectedBranch) : '#D31113';
  const isInBranch = currentSlide >= educationBlock.length;
  const sectionLabel = isInBranch ? getBranchLabel(selectedBranch) : 'Education Block';

  return (
    <GenderContext.Provider value={selectedGender}>
      <div
        ref={containerRef}
        className="h-screen w-screen bg-[#17072F] overflow-hidden relative slide-container"
        data-testid="presentation-container"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseMove={isFullscreen ? resetControlsTimer : undefined}
        onClick={isFullscreen ? resetControlsTimer : undefined}
      >
        {/* Noise overlay */}
        <div className="noise-overlay" />

        {/* Progress bar */}
        <div
          className="absolute top-0 left-0 h-1.5 z-50 transition-all duration-500 ease-out"
          style={{ width: `${progress}%`, backgroundColor: accentColor }}
          data-testid="progress-bar"
        />

        {/* Slide content */}
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentSlide + (showBranchSelector ? '-selector' : '') + (selectedBranch || '')}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.3 },
            }}
            className="absolute inset-0 z-10"
          >
            {currentSlideData?.type === 'branch_selector' ? (
              <BranchSelector onSelectBranch={handleSelectBranch} />
            ) : (
              renderSlide(currentSlideData, selectedGender)
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation controls - fade in fullscreen */}
        <div className={`transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>

        {/* Navigation buttons */}
        <div className="absolute bottom-6 right-6 sm:bottom-8 sm:right-8 flex items-center gap-2 sm:gap-4 z-50">
          {/* Fullscreen button */}
          <button
            onClick={toggleFullscreen}
            className="rounded-full bg-white/5 hover:bg-white/15 text-white p-3 sm:p-4 backdrop-blur-sm border border-white/10 transition-all duration-200"
            data-testid="fullscreen-btn"
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <Minimize className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize className="w-4 h-4 sm:w-5 sm:h-5" />}
          </button>
          {/* Home button (only show when in a branch) */}
          {selectedBranch && (
            <button
              onClick={handleReturnToSelector}
              className="rounded-full bg-white/5 hover:bg-white/15 text-white p-3 sm:p-4 backdrop-blur-sm border border-white/10 transition-all duration-200 hover:border-white/30"
              data-testid="branch-btn"
              aria-label="Return to branch selection"
            >
              <Home className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
          <button
            onClick={() => paginate(-1)}
            disabled={currentSlide === 0}
            className="rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed text-white p-3 sm:p-4 backdrop-blur-sm border border-white/10 transition-all duration-200"
            data-testid="prev-btn"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            onClick={() => paginate(1)}
            disabled={currentSlide === totalSlides - 1 && selectedBranch}
            className="rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed text-white p-3 sm:p-4 backdrop-blur-sm border border-white/10 transition-all duration-200"
            data-testid="next-btn"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Slide counter */}
        <div
          className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 text-white/50 font-mono text-base sm:text-lg tracking-widest font-bold z-50"
          data-testid="slide-counter"
        >
          <span className="text-white">{String(currentSlide + 1).padStart(2, '0')}</span>
          <span className="mx-2">/</span>
          <span>{String(totalSlides).padStart(2, '0')}</span>
        </div>

        {/* Section indicator with gender badge */}
        <div className="absolute top-6 right-4 sm:right-8 z-50 flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleResetGender}
            className="font-sans text-[10px] sm:text-xs uppercase tracking-wider font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/10 text-white/70 hover:bg-white/20 transition-colors"
          >
            {selectedGender === 'male' ? '♂ Male' : '♀ Female'}
          </button>
          <span 
            className="font-sans text-[10px] sm:text-sm uppercase tracking-widest font-semibold px-2 sm:px-4 py-1 sm:py-2 rounded-full transition-colors duration-300 hidden sm:inline"
            style={{ 
              backgroundColor: isInBranch ? `${accentColor}20` : 'rgba(255,255,255,0.1)',
              color: isInBranch ? accentColor : 'rgba(255,255,255,0.7)'
            }}
          >
            {sectionLabel}
          </span>
        </div>

        {/* Slide dots - hidden on very small screens */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-2 z-50">
          {currentSlides.map((slide, idx) => {
            const isEducation = idx < educationBlock.length;
            const dotColor = isEducation ? '#D31113' : accentColor;
            return (
              <button
                key={slide.id}
                onClick={() => goToSlide(idx)}
                className="transition-all duration-300"
                style={{
                  width: idx === currentSlide ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: idx === currentSlide ? dotColor : 'rgba(255,255,255,0.3)',
                }}
                data-testid={`dot-${idx}`}
              />
            );
          })}
        </div>

        {/* Keyboard hint */}
        <div className="absolute top-6 left-8 z-50 hidden md:block">
          <div className="flex items-center gap-2 text-white/30 text-sm font-mono">
            <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10">←</kbd>
            <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10">→</kbd>
            <span className="ml-2">Navigate</span>
            {selectedBranch && (
              <>
                <span className="mx-2">|</span>
                <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10">B</kbd>
                <span className="ml-2">Branches</span>
              </>
            )}
            <span className="mx-2">|</span>
            <kbd className="px-2 py-1 bg-white/5 rounded border border-white/10">G</kbd>
            <span className="ml-2">Gender</span>
          </div>
        </div>

        {/* Mobile swipe hint */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 sm:hidden z-50 text-white/20 text-xs font-mono">
          Swipe to navigate
        </div>

        </div>{/* End controls wrapper */}
      </div>
    </GenderContext.Provider>
  );
};
